import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CreditCard,
  Download,
  Calendar,
  DollarSign,
  Users,
  Target,
  CheckCircle
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

const planFeatures = {
  FREE: [
    'Up to 5 users',
    'Basic shot analysis',
    'Limited sessions (10/month)',
    'Community support'
  ],
  STARTER: [
    'Up to 25 users',
    'Advanced shot analysis',
    'Unlimited sessions',
    'Email support',
    'Basic analytics'
  ],
  PROFESSIONAL: [
    'Up to 100 users',
    'AI-powered insights',
    'Custom training programs',
    'Priority support',
    'Advanced analytics',
    'Team management tools'
  ],
  ENTERPRISE: [
    'Unlimited users',
    'Custom integrations',
    'Dedicated support',
    'Advanced reporting',
    'API access',
    'Custom branding',
    'SSO integration'
  ]
};

const planPricing = {
  FREE: { monthly: 0, annual: 0 },
  STARTER: { monthly: 29, annual: 290 },
  PROFESSIONAL: { monthly: 99, annual: 990 },
  ENTERPRISE: { monthly: 299, annual: 2990 }
};

export function BillingSettings() {
  const { organization } = useAuth();
  const currentPlan = organization?.plan || 'FREE';

  const mockInvoices = [
    {
      id: 'inv_001',
      date: '2024-01-15',
      amount: 99,
      status: 'paid',
      period: 'Jan 2024'
    },
    {
      id: 'inv_002',
      date: '2023-12-15',
      amount: 99,
      status: 'paid',
      period: 'Dec 2023'
    },
    {
      id: 'inv_003',
      date: '2023-11-15',
      amount: 99,
      status: 'paid',
      period: 'Nov 2023'
    }
  ];

  const mockUsage = {
    users: 32,
    maxUsers: currentPlan === 'FREE' ? 5 : currentPlan === 'STARTER' ? 25 : currentPlan === 'PROFESSIONAL' ? 100 : 999,
    sessions: 156,
    maxSessions: currentPlan === 'FREE' ? 10 : -1,
    storage: 2.4, // GB
    maxStorage: 10 // GB
  };

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <CreditCard className="mr-2 h-5 w-5" />
            Current Plan
          </CardTitle>
          <CardDescription>
            Your current subscription and usage
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-2xl font-bold">{currentPlan}</h3>
                  <p className="text-muted-foreground">
                    {currentPlan === 'FREE' ? 'Free forever' : `$${planPricing[currentPlan as keyof typeof planPricing].monthly}/month`}
                  </p>
                </div>
                <Badge variant="success">Active</Badge>
              </div>
              
              <div className="space-y-2">
                {planFeatures[currentPlan as keyof typeof planFeatures].map((feature, index) => (
                  <div key={index} className="flex items-center text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                    {feature}
                  </div>
                ))}
              </div>

              <div className="flex gap-2 mt-6">
                <Button>Upgrade Plan</Button>
                <Button variant="outline">View All Plans</Button>
              </div>
            </div>

            {/* Usage Stats */}
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Users</span>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="text-2xl font-bold">
                  {mockUsage.users}
                  <span className="text-sm font-normal text-muted-foreground">
                    {mockUsage.maxUsers === 999 ? '' : ` / ${mockUsage.maxUsers}`}
                  </span>
                </div>
                {mockUsage.maxUsers !== 999 && (
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-primary h-2 rounded-full" 
                      style={{ width: `${(mockUsage.users / mockUsage.maxUsers) * 100}%` }}
                    />
                  </div>
                )}
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Sessions</span>
                  <Target className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="text-2xl font-bold">
                  {mockUsage.sessions}
                  {mockUsage.maxSessions > 0 && (
                    <span className="text-sm font-normal text-muted-foreground">
                      / {mockUsage.maxSessions}
                    </span>
                  )}
                </div>
                {mockUsage.maxSessions > 0 && (
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-primary h-2 rounded-full" 
                      style={{ width: `${(mockUsage.sessions / mockUsage.maxSessions) * 100}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Method */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Method</CardTitle>
          <CardDescription>
            Manage your payment information
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-8 bg-gray-200 rounded flex items-center justify-center">
                <CreditCard className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium">•••• •••• •••• 4242</p>
                <p className="text-sm text-muted-foreground">Expires 12/25</p>
              </div>
            </div>
            <Button variant="outline">Update</Button>
          </div>
        </CardContent>
      </Card>

      {/* Billing History */}
      <Card>
        <CardHeader>
          <CardTitle>Billing History</CardTitle>
          <CardDescription>
            Download your previous invoices and receipts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {mockInvoices.map((invoice) => (
              <div key={invoice.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <DollarSign className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">${invoice.amount}</p>
                    <p className="text-sm text-muted-foreground">
                      {invoice.period} • {new Date(invoice.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="success">Paid</Badge>
                  <Button variant="ghost" size="sm">
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Next Billing */}
      <Card>
        <CardHeader>
          <CardTitle>Next Billing Date</CardTitle>
          <CardDescription>
            Your next payment is scheduled for
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-3">
            <Calendar className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="font-medium">February 15, 2024</p>
              <p className="text-sm text-muted-foreground">
                ${planPricing[currentPlan as keyof typeof planPricing].monthly} will be charged to your payment method
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}